<script setup lang="ts">
import {computed, ref} from "vue";
import {useMedicationCheckupStore} from "@/medication-checkup/stores/medication-checkup-store";
import MedicationCheckupCopiloteLine from "@/medication-checkup/components/MedicationCheckupCopiloteLine.vue";
import MedicationCheckupUserEcho from "@/medication-checkup/components/MedicationCheckupUserEcho.vue";
import MedicationCheckupAnalysisClosingCard from "@/medication-checkup/components/MedicationCheckupAnalysisClosingCard.vue";
import MSSanteDialog from "@/mssante/components/MSSanteDialog.vue";
import MedicationCheckupCopiloteBubble from "@/medication-checkup/components/MedicationCheckupCopiloteBubble.vue";
import MedicationCheckupSceneReplies from "@/medication-checkup/components/MedicationCheckupSceneReplies.vue";
import {MedicationCheckupAction} from "@/medication-checkup/interfaces";
import {AnalysisClosingAnswers, AnalysisClosingView} from "@/medication-checkup/interfaces/analysis-closing";
import {ACTION_DRAFT} from "@/medication-checkup/constants";
import {SceneProps, useScene} from "@/medication-checkup/composables/useScene";
import {medicationCheckupApi} from "@/container";
import {useDoctorSynthesisMessage} from "@/medication-checkup/composables/useDoctorSynthesisMessage";
import {useToastStore} from "@/stores/toast.store";
import fileHelper from "@/core/helpers/fileHelper";
import {closingDocumentFilename} from "@/medication-checkup/composables/useClosingDocument";

/**
 * The scene closing the analysis of the treatments: the end of the second
 * step.
 *
 * Everything it shows is served by the handler: the words, the before /
 * after recap read from the interventions, the two fields, the conclusion
 * the AI drafted and — once validated — the texts recorded, the line
 * carrying the course over to the advice interview, the recap to download
 * and the synthesis to send to the doctor. Typing is the scene's own state:
 * the closing action, always available since both texts are optional,
 * submits them at once.
 *
 * Landing on the live block asks the server for the draft, the way the
 * closing of the collection does: the proposal comes back in the served
 * view, and the pharmacist's typing takes over it from there.
 *
 * Sending the synthesis to the doctor belongs to the transmission, the block
 * that follows. It stays here only while the course of the cycle does not
 * hold that block yet: the transmission is laid out on the cycles by a seeder
 * run by hand, and until then this scene is the only way to send.
 */

const props = defineProps<SceneProps<AnalysisClosingView>>();

/**
 * The draft is asked for as the scene opens, so its wait overlaps the
 * typing of the bubble.
 */
const {bubbleShown, cardShown, clicked, view, text, actions, closing, acted, act: submit} = useScene(props, {
    card: true,
    live: () => {
        if (view.value?.conclusion === null && view.value?.conclusion_provenance === null) {
            draft();
        }
    }
});

const store = useMedicationCheckupStore();

const downloading = ref(false);

/**
 * Whether the draft of the conclusion is being asked for.
 */
const drafting = ref(false);

/**
 * The texts typed so far — local until the closing action submits them. The
 * conclusion stays null until the pharmacist types into it, so the proposal
 * served in the meantime shows through.
 */
const answers = ref<AnalysisClosingAnswers>({questions: '', conclusion: null});

/**
 * Pending animation timers, cancelled on unmount so they never land on a
 * scene that is gone.
 */
/**
 * The conclusion as it stands: what the pharmacist typed, else the proposal
 * the server serves, else nothing.
 */
const conclusion = computed(() => answers.value.conclusion ?? view.value?.conclusion ?? '');

/**
 * The texts the card shows while live, the conclusion resolved.
 */
const effective = computed<AnalysisClosingAnswers>(() => ({
    questions: answers.value.questions,
    conclusion: conclusion.value
}));

/**
 * Ask the server for the draft of the conclusion.
 *
 * The answered cycle carries the proposal in the served view — nothing to
 * seed locally. A failed ask leaves the field empty and the closing goes on
 * by hand: the store has already reported why.
 */
const draft = async () => {
    drafting.value = true;

    try {
        await store.submitBlock(props.block.id, {action: ACTION_DRAFT});
    } catch {
        // Reported by the store; the pharmacist concludes without a proposal.
    } finally {
        drafting.value = false;
    }
};

/**
 * Type into one field — local only: the closing action submits both.
 */
const typed = (field: keyof AnalysisClosingAnswers, value: string) => {
    answers.value = {...answers.value, [field]: value};
};

/**
 * Take the closing action, submitting the two texts.
 */
const act = (action: MedicationCheckupAction) => submit(action, {
    questions: answers.value.questions.trim() || null,
    conclusion: conclusion.value.trim() || null
});

/**
 * Whether the scene still sends the synthesis: only while the step shown
 * holds no transmission block.
 */
const sendable = computed(() => {
    return !store.displayedBlocks.some(block => block.definition_key === 'doctor_transmission');
});

const {customer, open: mssanteOpen, prefills} = useDoctorSynthesisMessage(
    () => view.value?.doctor_synthesis,
    () => view.value?.conclusion,
    () => view.value?.sender
);

/**
 * Who the synthesis went to, once it has. Nothing records the send, so this
 * only holds while the scene is mounted: it tells the pharmacist what they
 * just did, not what the cycle knows.
 */
const sentTo = ref<string | null>(null);

/**
 * How the card names the recipient: « au Dr Lambert » for a professional the
 * directory named — the surname alone, the way one speaks of a colleague —
 * and the address itself when it was typed by hand, a mailbox taking no
 * title.
 */
const sentLabel = computed<string | null>(() => {
    const recipient = sentTo.value?.trim();

    if (!recipient) {
        return null;
    }

    if (recipient.includes('@')) {
        return `à ${recipient}`;
    }

    return `au Dr ${recipient.split(/\s+/).pop()}`;
});

/**
 * Download the recap the closing serves, saved under a dated name: the HDS
 * renders it on request, so the wait is the generation itself.
 */
const download = async () => {
    const recap = view.value?.analysis_recap;

    if (!recap || downloading.value) {
        return;
    }

    downloading.value = true;

    try {
        const blob = await medicationCheckupApi().downloadDocument(recap.url);
        fileHelper.downloadBlob(closingDocumentFilename(recap, 'recapitulatif-analyse'), blob);
    } catch {
        useToastStore().showError({content: "Le récapitulatif n'a pas pu être téléchargé."});
    } finally {
        downloading.value = false;
    }
};
</script>

<template>
  <div class="flex flex-col gap-3.5">
    <template v-if="frozen">
      <MedicationCheckupCopiloteLine v-if="text" :text="text" />
      <MedicationCheckupAnalysisClosingCard
        v-if="view"
        :view="view"
        :downloading="downloading"
        :sent="sentLabel"
        :sendable="sendable"
        frozen
        @download="download"
        @mssante="mssanteOpen = true"
      />
      <MedicationCheckupUserEcho v-if="acted" :text="acted" />
      <MedicationCheckupCopiloteLine v-if="closing" :text="closing" />
    </template>

    <template v-else>
      <MedicationCheckupCopiloteBubble
        :shown="bubbleShown"
        :text="text"
      />

      <MedicationCheckupAnalysisClosingCard
        v-if="cardShown && view"
        :view="view"
        :answers="effective"
        :drafting="drafting"
        class="animate-message-in"
        @typed="typed"
      />

      <MedicationCheckupSceneReplies
        :clicked="clicked"
        :actions="actions"
        :shown="cardShown"
        :disabled="drafting"
        @act="act"
      />
    </template>

    <MSSanteDialog
      v-if="sendable && view?.doctor_synthesis"
      v-model:open="mssanteOpen"
      :prefills="prefills"
      :consumer-key="customer?.consumer_key"
      :patient-name="customer?.information?.name"
      :source="store.cycle ? {type: 'medication-checkup', id: store.cycle.checkup_id} : undefined"
      @sent="sentTo = $event"
    />
  </div>
</template>
